import { HttpEventType } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import {
    ClientErrorCodes,
    IPackageInfo,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { NugetServiceFactory } from '@pages/packages/shared/nuget.util';
import {
    FileUploadService,
    LibraryService,
    ModalService,
    PackageService,
} from '@services';
import { unsubscribe } from '@utils/object.utils';

import {
    BehaviorSubject,
    Observable,
    of,
    Subject,
    Subscription,
} from 'rxjs';
import {
    auditTime,
    delay,
    distinctUntilChanged,
    finalize,
    map,
    pairwise,
    startWith,
    switchMap,
} from 'rxjs/operators';

import { NugetUpgradeModalComponent } from '../nuget-upgrade/nuget-upgrade.modal';

const TRANSITION_SPEED = 250;

interface IBulkResponse {
    value: IBulkItemServerResponse[];
}

interface IBulkItemServerResponse {
    Key: string;
    Status: number;
    Body: string;
}

interface IBulkItemResponse {
    key: string;
    status: string | number;
    body: IPackageError | IPackageServerInfo;
}

interface IPackageServerInfo {
    Id: string;
    Version: string;
}

interface IPackageError {
    ErrorCode: number;
    Message: string;
    ResourceIds?: number[];
}

@Component({
    templateUrl: './nuget-upload.modal.html',
    styleUrls: [
        '../../../../../styles/modal/file-upload.modal.scss',
        './nuget-upload.modal.scss',
    ],
    providers: [
        LibraryService,
        PackageService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NugetUploadModalComponent
    extends UiModalComponent<any>
    implements OnInit, OnDestroy {

    public files: File[] = [];
    public type!: string;

    public loading$ = new BehaviorSubject(false);
    public progress$!: Observable<number>;
    public showProgressBar$!: Observable<boolean>;

    public get fileNames() {
        return this.files.map(file => file.name).join(', ');
    }

    private _allowedFileExtensions: string[] = ['.nupkg'];
    private _uploadSub?: Subscription;
    private _nugetService = this._injector.get(NugetServiceFactory(this.context.additionalData.type));
    private _modalService = this._injector.get<ModalService>(ModalService);

    private _fileUploadService!: FileUploadService;
    private _translateService!: TranslateService;
    private _progressSource = new Subject<number>();

    private _upgradeChecked = false;

    ngOnInit() {
        // hide progress for fast uploads
        // and show the progress bar for multiple progress states
        this.showProgressBar$ = this._progressSource
            .pipe(
                startWith(0),
                map(progress => !!progress),
                distinctUntilChanged(),
                pairwise(),
                switchMap(
                    ([previous, current]) => {
                        return of(current)
                            .pipe(
                                delay(
                                    !previous &&
                                        current ?
                                        TRANSITION_SPEED :
                                        0,
                                ),
                            );
                    },
                ),
            );

        // update progress according to the progress-bar transition speed
        this.progress$ = this._progressSource
            .pipe(
                auditTime(TRANSITION_SPEED),
            );

        this._fileUploadService = this._injector.get<FileUploadService>(FileUploadService);
        this._translateService = this._injector.get<TranslateService>(TranslateService);

        this.type = this.context.additionalData.type;
    }

    ngOnDestroy() {
        unsubscribe(
            this._uploadSub,
        );
    }

    public onClear() {
        this.files = [];
    }

    public onFileChange(files: File[]) {
        this.files = files;
    }

    public onSubmit() {
        const areFilesValid = this.files.some(
            file => this._fileUploadService.isFileItemValid(file, this._allowedFileExtensions),
        );
        if (!areFilesValid) {
            this._snack.error(
                `${this._translateService.instant(
                    `${this.type}_upload_package_modal_file_input_warning`)} (#${
                ClientErrorCodes.InvalidFile
                })`,
            );
            return;
        }

        this.loading$.next(true);

        this._uploadSub = this._nugetService.uploadPackages(this.files)
            .pipe(
                finalize(() => {
                    this.loading$.next(false);
                    this._progressSource.next(0);
                }),
            )
            .subscribe((ev) => {
                if (ev.type === HttpEventType.UploadProgress) {
                    this._progressSource.next(
                        Math.round((ev.loaded * 100) / ev.total!),
                    );
                }

                if (ev.type === HttpEventType.Response) {
                    const body = ((ev.body as IBulkResponse).value)
                        .map(response => ({
                            key: response.Key,
                            status: response.Status,
                            body: JSON.parse(response.Body),
                        } as IBulkItemResponse));
                    const success = body.filter(response => response.status === 200 || response.status === 'OK');
                    const error = body.filter(response => response.status !== 200 && response.status !== 'OK');

                    // success
                    if (success && success.length) {
                        this._dialogRef.close({
                            type: ModalResultType.Add,
                        });

                        if (this._upgradeChecked) {
                            this._modalService
                                .openModal(NugetUpgradeModalComponent, null, {
                                    data: {
                                        packages: success.map(data => ({
                                            id: (data.body as IPackageServerInfo).Id,
                                            version: (data.body as IPackageServerInfo).Version,
                                        }) as IPackageInfo),
                                        type: this.type,
                                    },
                                })
                                .afterClosedResult();
                        }
                    }

                    // we have failed items:
                    if (error && error.length) {
                        const errorMessage = error.map(bulkitem => {
                            const packageError = bulkitem.body as IPackageError;
                            return `${bulkitem.key}: ${packageError.Message} (#${packageError.ErrorCode})`;
                        }).join('\n');

                        this._snack.error(errorMessage);

                        // if we successfully uploaded some items, reload the grid
                        if (this.files.length - error.length > 0) {
                            this.customEvent.emit({
                                type: ModalResultType.Update,
                            });
                        }

                        // remove the uploaded files from the form (for resubmitting?)
                        this.files = this.files.filter(
                            file => error.find(i => i.key === file.name),
                        );
                    }
                }
            });
    }

    public checkUpgradeValue(event: { checked: boolean; }) {
        this._upgradeChecked = event.checked;
    }
}
